import {
    CoreFetch,
    EvidenceApi,
    GetProviderEvidenceListOrderEnum,
    GetProviderEvidenceListSortEnum,
    GetProviderEvidenceMissingListOrderEnum,
    GetProviderEvidenceMissingListSortEnum,
    ProviderEvidence,
    ProviderMissingEvidence,
    createRequestFilters,
    ICustomFilter,
} from '@enter/core';
import { action, makeObservable, observable, runInAction } from 'mobx';

export interface IEvidenceListing {
    evidence: ProviderEvidence[];
    limit?: number;
    offset?: number;
    total?: number;
}

export interface IEvidenceListingRequest {
    page?: number;
    pageSize?: number;
    filters?: ICustomFilter[];
    sort?: GetProviderEvidenceListSortEnum[];
    order?: GetProviderEvidenceListOrderEnum[];
    filterExperimentId?: string[];
}

export interface IMissingEvidenceListing {
    missingEvidence: ProviderMissingEvidence[];
    limit?: number;
    offset?: number;
    total?: number;
}

export interface IMissingEvidenceListingRequest {
    page?: number;
    pageSize?: number;
    filters?: ICustomFilter[];
    sort?: GetProviderEvidenceMissingListSortEnum[];
    order?: GetProviderEvidenceMissingListOrderEnum[];
    filterExperimentId?: string[];
}

export interface ISortRequest {
    sort: string[];
    order: string[];
}

export interface FetchOneByOffsetResult {
    evidence: ProviderEvidence | null;
    total: number;
}

export interface EvidencePaginationState {
    offset: number;
    total: number;
    filters?: ICustomFilter[];
    sortRequest?: ISortRequest;
}

export class EvidenceStore {
    @observable public evidence: ProviderEvidence[] = [];
    @observable public missingEvidence: ProviderMissingEvidence[] = [];
    @observable public loading: boolean = false;
    @observable public missingEvidenceLoading: boolean = false;
    @observable public total: number = 0;
    @observable public missingEvidenceTotal: number = 0;
    @observable public paginationState: EvidencePaginationState | null = null;

    constructor() {
        makeObservable(this);
    }

    @action
    public setPaginationState = (state: EvidencePaginationState): void => {
        this.paginationState = state;
    };

    @action
    public clearPaginationState = (): void => {
        this.paginationState = null;
    };

    @action
    public updatePaginationOffset = (offset: number, total?: number): void => {
        if (this.paginationState) {
            this.paginationState = {
                ...this.paginationState,
                offset,
                total: total ?? this.paginationState.total,
            };
        }
    };

    @action
    public async loadEvidenceList(request?: IEvidenceListingRequest): Promise<IEvidenceListing> {
        this.loading = true;
        try {
            const limit = request?.pageSize ?? 25;
            const offset = request?.page && request?.pageSize ? request.page * request.pageSize : 0;
            const sort = request?.sort && request.sort.length > 0 ? request.sort : [GetProviderEvidenceListSortEnum.CREATED];
            const order = request?.order && request.order.length > 0 ? request.order : [GetProviderEvidenceListOrderEnum.DESCENDING];
            const filters = request?.filters ? createRequestFilters(request.filters, true) : {};

            const raw = await CoreFetch<EvidenceApi>(EvidenceApi).getProviderEvidenceListRaw({
                limit,
                offset,
                sort,
                order,
                ...filters,
                ...(request?.filterExperimentId ? { filterExperimentId: request.filterExperimentId.map(id => `EQ:${id}`) } : {}),
            });

            const evidence = await raw.value();
            const paginationTotal = raw.raw.headers.get('pagination-total');
            const total = paginationTotal ? parseInt(paginationTotal, 10) : evidence.length;
            const resolvedTotal = Number.isNaN(total) ? evidence.length : total;

            runInAction(() => {
                this.evidence = evidence;
                this.total = resolvedTotal;
                this.loading = false;
            });

            return {
                evidence,
                limit,
                offset,
                total: resolvedTotal,
            };
        } catch (error) {
            runInAction(() => {
                this.loading = false;
            });
            throw error;
        }
    }

    @action
    public async getEvidence(evidenceId: string): Promise<ProviderEvidence> {
        const evidence = await CoreFetch<EvidenceApi>(EvidenceApi).getProviderEvidence({
            id: evidenceId,
        });

        runInAction(() => {
            // Update or add to evidence list
            const index = this.evidence.findIndex((e) => e.id === evidenceId);
            if (index >= 0) {
                this.evidence[index] = evidence;
            } else {
                this.evidence = [...this.evidence, evidence];
            }
        });

        return evidence;
    }

    @action
    public async loadMissingEvidenceList(request?: IMissingEvidenceListingRequest): Promise<IMissingEvidenceListing> {
        this.missingEvidenceLoading = true;
        try {
            const limit = request?.pageSize ?? 25;
            const offset = request?.page && request?.pageSize ? request.page * request.pageSize : 0;
            const sort = request?.sort && request.sort.length > 0 ? request.sort : [GetProviderEvidenceMissingListSortEnum.EVIDENCETYPEID];
            const order = request?.order && request.order.length > 0 ? request.order : [GetProviderEvidenceMissingListOrderEnum.DESCENDING];

            const raw = await CoreFetch<EvidenceApi>(EvidenceApi).getProviderEvidenceMissingListRaw({
                limit,
                offset,
                sort,
                order,
                ...(request?.filterExperimentId ? { filterExperimentId: request.filterExperimentId.map(id => `EQ:${id}`) } : {}),
            });

            const missingEvidence = await raw.value();
            const paginationTotal = raw.raw.headers.get('pagination-total');
            const total = paginationTotal ? parseInt(paginationTotal, 10) : missingEvidence.length;
            const resolvedTotal = Number.isNaN(total) ? missingEvidence.length : total;

            runInAction(() => {
                this.missingEvidence = missingEvidence;
                this.missingEvidenceTotal = resolvedTotal;
                this.missingEvidenceLoading = false;
            });

            return {
                missingEvidence,
                limit,
                offset,
                total: resolvedTotal,
            };
        } catch (error) {
            runInAction(() => {
                this.missingEvidenceLoading = false;
            });
            throw error;
        }
    }

    public fetchOneByOffset = async (
        offset: number,
        filters?: ICustomFilter[],
        sortRequest?: ISortRequest,
    ): Promise<FetchOneByOffsetResult> => {
        const sort = (sortRequest?.sort.filter(s => 
            Object.values(GetProviderEvidenceListSortEnum).includes(s as GetProviderEvidenceListSortEnum)
        ) || []) as GetProviderEvidenceListSortEnum[];
        
        const order = (sortRequest?.order.filter(o => 
            Object.values(GetProviderEvidenceListOrderEnum).includes(o as GetProviderEvidenceListOrderEnum)
        ) || []) as GetProviderEvidenceListOrderEnum[];

        const finalSort: GetProviderEvidenceListSortEnum[] = sort.length > 0 ? sort : [GetProviderEvidenceListSortEnum.CREATED];
        const finalOrder: GetProviderEvidenceListOrderEnum[] = order.length > 0 ? order : [GetProviderEvidenceListOrderEnum.DESCENDING];

        const reqFilters = filters ? createRequestFilters(filters, true) : {};
        
        const raw = await CoreFetch<EvidenceApi>(EvidenceApi).getProviderEvidenceListRaw({
            limit: 1,
            offset,
            sort: finalSort,
            order: finalOrder,
            ...reqFilters,
        });

        const evidence = await raw.value();
        const paginationTotal = raw.raw.headers.get('pagination-total');
        const total = paginationTotal ? parseInt(paginationTotal, 10) : evidence.length;
        const resolvedTotal = Number.isNaN(total) ? evidence.length : total;

        return {
            evidence: evidence.length > 0 ? evidence[0] : null,
            total: resolvedTotal,
        };
    };
}

